// Modal showing one serial's full traceability: import receipt (where/when/cost),
// transfers, and the sale order (which order / customer / price).
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { X, PackagePlus, ArrowLeftRight, ShoppingCart, Loader2 } from "lucide-react";
import { apiClient } from "@/lib/api/client";
import { formatDate, formatDateTime } from "@/lib/format/date";
import { Button } from "@/components/ui/button";

interface ImportInfo {
  id?: string | null;
  code?: string | null;
  importedAt?: string | null;
  warehouseName?: string | null;
  supplierName?: string | null;
  unitCost?: string | null;
}
interface SaleInfo {
  orderId?: string | null;
  orderCode?: string | null;
  orderType?: string | null;
  soldAt?: string | null;
  customerName?: string | null;
  customerPhone?: string | null;
  unitPrice?: string | null;
  discount?: string | null;
}
interface TransferInfo {
  at: string;
  from?: string | null;
  to?: string | null;
}
interface SerialHistory {
  serial: {
    serialNo: string;
    status: string;
    productName?: string | null;
    variantSku?: string | null;
    warrantyStart?: string | null;
    warrantyEnd?: string | null;
  };
  import?: ImportInfo | null;
  sale?: SaleInfo | null;
  transfers: TransferInfo[];
}

const STATUS_LABELS: Record<string, string> = {
  imported: "Nhập về", in_stock: "Tồn kho", sold: "Đã bán", in_repair: "Đang sửa",
  rma_to_vendor: "RMA nhà cung cấp", defective: "Hỏng", returned: "Hoàn trả",
};
const ORDER_TYPE_LABEL: Record<string, string> = { retail: "Bán lẻ", wholesale: "Bán sỉ" };

function fmtVND(s?: string | null) {
  if (s == null) return "—";
  const n = parseFloat(s);
  if (isNaN(n)) return "—";
  return new Intl.NumberFormat("vi-VN").format(n) + " ₫";
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="text-right font-medium">{children}</span>
    </div>
  );
}

export function SerialHistoryModal({ serialNo, onClose }: { serialNo: string; onClose: () => void }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["serial-history", serialNo],
    queryFn: () =>
      apiClient.get<{ data: SerialHistory }>(`/v1/serials/${encodeURIComponent(serialNo)}/history`).then((r) => r.data.data),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-xl border bg-card p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold">Lịch sử IMEI</h2>
            <p className="font-mono text-sm text-muted-foreground">{serialNo}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}><X size={18} /></Button>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground">
            <Loader2 size={18} className="animate-spin" /> Đang tải…
          </div>
        )}
        {isError && <p className="py-6 text-center text-destructive">Không tải được lịch sử.</p>}

        {data && (
          <div className="space-y-4">
            {/* Product summary */}
            <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
              <Row label="Sản phẩm">{data.serial.productName ?? "—"}</Row>
              <Row label="SKU"><span className="font-mono text-xs">{data.serial.variantSku ?? "—"}</span></Row>
              <Row label="Trạng thái">{STATUS_LABELS[data.serial.status] ?? data.serial.status}</Row>
              {data.serial.warrantyEnd && (
                <Row label="Bảo hành đến">
                  {formatDate(data.serial.warrantyEnd)}
                  {data.serial.warrantyStart ? ` (từ ${formatDate(data.serial.warrantyStart)})` : ""}
                </Row>
              )}
            </div>

            {/* Import */}
            <section className="space-y-2">
              <h3 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                <PackagePlus size={15} className="text-green-600" /> Nhập hàng
              </h3>
              {data.import ? (
                <div className="rounded-lg border p-3 space-y-2">
                  <Row label="Phiếu nhập">
                    {data.import.id ? (
                      <Link
                        to="/inventory/imports/$id"
                        params={{ id: data.import.id }}
                        onClick={onClose}
                        className="font-mono text-primary hover:underline"
                      >
                        {data.import.code ?? "—"}
                      </Link>
                    ) : (
                      <span className="font-mono">{data.import.code ?? "—"}</span>
                    )}
                  </Row>
                  <Row label="Nhà cung cấp">{data.import.supplierName ?? "—"}</Row>
                  <Row label="Kho nhập">{data.import.warehouseName ?? "—"}</Row>
                  <Row label="Ngày nhập">{data.import.importedAt ? formatDateTime(data.import.importedAt) : "—"}</Row>
                  <Row label="Giá nhập"><span className="text-green-700">{fmtVND(data.import.unitCost)}</span></Row>
                </div>
              ) : (
                <p className="rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
                  Không có thông tin phiếu nhập (dữ liệu cũ từ nhanh.vn).
                </p>
              )}
            </section>

            {/* Transfers */}
            {data.transfers.length > 0 && (
              <section className="space-y-2">
                <h3 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                  <ArrowLeftRight size={15} className="text-amber-600" /> Chuyển kho
                </h3>
                <div className="space-y-1">
                  {data.transfers.map((t, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 rounded-md border px-3 py-1.5 text-sm">
                      <span>{t.from ?? "—"} → <strong>{t.to ?? "—"}</strong></span>
                      <span className="text-xs text-muted-foreground">{formatDateTime(t.at)}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Sale */}
            <section className="space-y-2">
              <h3 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                <ShoppingCart size={15} className="text-blue-600" /> Bán ra
              </h3>
              {data.sale ? (
                <div className="rounded-lg border p-3 space-y-2">
                  <Row label="Đơn hàng">
                    {data.sale.orderId ? (
                      <Link
                        to="/orders/$id"
                        params={{ id: data.sale.orderId }}
                        onClick={onClose}
                        className="font-mono text-primary hover:underline"
                      >
                        {data.sale.orderCode ?? "—"}
                      </Link>
                    ) : (
                      <span className="font-mono">{data.sale.orderCode ?? "—"}</span>
                    )}
                  </Row>
                  <Row label="Loại đơn">{data.sale.orderType ? (ORDER_TYPE_LABEL[data.sale.orderType] ?? data.sale.orderType) : "—"}</Row>
                  <Row label="Khách hàng">
                    {data.sale.customerName ?? "—"}
                    {data.sale.customerPhone ? <span className="ml-1 font-mono text-xs text-muted-foreground">{data.sale.customerPhone}</span> : null}
                  </Row>
                  <Row label="Ngày bán">{data.sale.soldAt ? formatDateTime(data.sale.soldAt) : "—"}</Row>
                  <Row label="Giá bán"><span className="text-blue-700">{fmtVND(data.sale.unitPrice)}</span></Row>
                  {data.sale.discount && parseFloat(data.sale.discount) > 0 && (
                    <Row label="Giảm giá">{fmtVND(data.sale.discount)}</Row>
                  )}
                </div>
              ) : (
                <p className="rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
                  Chưa bán (đang tồn kho).
                </p>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
